import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, PlayCircleIcon, LockIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Seo } from '../components/Seo';
import { RegistrationForm } from '../components/webinars/RegistrationForm';
import { sessionDateLabel } from '../components/webinars/SessionTime';
import { pastWebinars, nextWebinar } from '../data/webinars';
import type { Webinar } from '../data/webinars';
import { breadcrumbSchema } from '../lib/seo';
export function WebinarReplays() {
  const replays = pastWebinars();
  const upcoming = nextWebinar();
  const [open, setOpen] = React.useState<Webinar | null>(null);
  const [urls, setUrls] = React.useState<Record<string, string>>({});
  return <>
      <Seo title="Webinar replays for government" description="Watch recordings of our webinars for government officials on plan delivery, government data and responsible AI. Each recording opens after a short registration." path="/webinars/replays" jsonLd={[breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Webinars',
      path: '/webinars'
    }, {
      name: 'Replays',
      path: '/webinars/replays'
    }])]} data-id="element-3487" data-mp="1zl03p2gv9pcw" />

      <section className="surface-dark" data-id="element-3488" data-mp="2cz46a36g8lkt">
        <Container className="py-12 sm:py-16" data-id="element-3489" data-mp="30q9ga1ppkjn7">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-3490" data-mp="30rkpih1xajlp">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-3491" data-mp="35q5oye9coz93">
              <li data-id="element-3492" data-mp="2qt4va57lmu7z">
                <Link to="/" className="hover:text-white" data-id="element-3493" data-mp="2qrlg6hxoip7s">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-3494" data-mp="2vpv9so3r9hra">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-3495" data-mp="1fdpz33n7s6dx" />
              </li>
              <li data-id="element-3496" data-mp="1ffsy2d6ch9us">
                <Link to="/webinars" className="hover:text-white" data-id="element-3497" data-mp="1kcb0h6e7cw3y">
                  Webinars
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-3498" data-mp="3my6nk7vj945d">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-3499" data-mp="3n1azjrf8p46l" />
              </li>
              <li aria-current="page" className="text-white/80" data-id="element-3500" data-mp="3i0n6z1bd4fa9">
                Replays
              </li>
            </ol>
          </nav>

          <Kicker data-id="element-3501" data-mp="38abu2p9pavr8">Replay library</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[32px] font-light leading-tight text-white sm:text-[46px]" data-id="element-3502" data-mp="389iss3tigygh">
            Every session, recorded.
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-3503" data-mp="2z945x00wfbav">
            Each recording comes with its one page summary. Tell us who you are once and the recording plays
            here, with the summary sent to your inbox.
          </p>
        </Container>
      </section>

      <Section aria-labelledby="library" data-id="element-3504" data-mp="sj6009buufpb">
        <SectionHeader id="library" kicker={replays.length > 0 ? `${replays.length} available` : 'Coming soon'} title={replays.length > 0 ? 'Choose a session.' : 'The first recordings arrive in January.'} lead={replays.length > 0 ? undefined : 'The 2027 programme opens on Thursday 21 January. Recordings are published here within two working days of each session, so this library fills as the year runs.'} data-id="element-3505" data-mp="si4pq72nxx8n" />

        {replays.length === 0 ? <div className="mt-10 rounded-md border border-line bg-mist p-8" data-id="element-3506" data-mp="19ucohkehpqid">
            <div className="flex items-start gap-3" data-id="element-3507" data-mp="c6ko6hk4y96k">
              <PlayCircleIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-3508" data-mp="h3b494vxx4rz" />
              <div data-id="element-3509" data-mp="m2eautrkjj22">
                <h3 className="font-display text-[21px] text-ink" data-id="element-3510" data-mp="m1w3b0zghieu">
                  Register for a live session instead
                </h3>
                <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-3511" data-mp="qzxnjp8mbnrw">
                  Attending live is worth more than the recording: you can put your own situation to us in the
                  session. Registrants receive the recording and the summary afterwards in any case, whether or
                  not they attend.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-3512" data-mp="1w1p66toihcze">
                  {upcoming && <Button to={`/webinars/${upcoming.slug}`} data-id="element-3513" data-mp="2a1q2rwxqty92">Register for the {upcoming.month} session</Button>}
                  <Button to="/webinars" variant="secondary" data-id="element-3514" data-mp="250aja9b7rzss">
                    See the full calendar
                  </Button>
                </div>
              </div>
            </div>
          </div> : <div className="mt-10 space-y-px bg-line" data-id="element-3515" data-mp="2exyoxuacyrpq">
            {replays.map(webinar => {
          const url = urls[webinar.slug];
          const isOpen = open?.slug === webinar.slug;
          return <article key={webinar.slug} className="bg-panel p-6 sm:p-8" data-id="element-3516" data-mp="32p43850nhbxx">
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between" data-id="element-3517" data-mp="32mzv1avpxqru">
                    <div className="max-w-2xl" data-id="element-3518" data-mp="3hc2u9yvli4c4">
                      <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-3519" data-mp="3hddz820s1i4t">
                        {sessionDateLabel(webinar)}
                      </p>
                      <h3 className="mt-3 font-display text-[22px] leading-snug text-ink sm:text-[25px]" data-id="element-3520" data-mp="37e54v82021hx">
                        {webinar.title}
                      </h3>
                      {/* The description stays in the markup, so the gate never removes it from the page. */}
                      <p className="mt-3 text-[16px] text-body" data-id="element-3521" data-mp="32es8nwp8ni2o">{webinar.description}</p>
                      <p className="mt-3 text-small text-muted" data-id="element-3522" data-mp="32gmxhm3mruc1">
                        {webinar.focus} · {webinar.audience}
                      </p>
                    </div>

                    {!url && <Button onClick={() => setOpen(isOpen ? null : webinar)} variant={isOpen ? 'secondary' : 'primary'} className="shrink-0" aria-expanded={isOpen} data-id="element-3523" data-mp="2ep7ojahcmert">
                        <LockIcon className="mr-2 h-4 w-4" aria-hidden="true" data-id="element-3524" data-mp="20lij7ofv3ly9" />
                        {isOpen ? 'Close' : 'Watch the recording'}
                      </Button>}
                  </div>

                  {url && <figure className="mt-6 overflow-hidden rounded-md border border-line" data-id="element-3525" data-mp="1lvuf6fgaq96u">
                      <video controls autoPlay preload="metadata" playsInline className="block aspect-video w-full bg-charcoal" data-id="element-3526" data-mp="2jhid54segzkh">
                        <source src={url} type="video/mp4" data-id="element-3527" data-mp="24lj67xh115gx" />
                        Your browser cannot play embedded video.
                      </video>
                      <figcaption className="bg-mist px-5 py-4 text-small text-muted" data-id="element-3528" data-mp="y6i9hpk6gj9a">
                        This link is signed for you and expires after fifteen minutes.
                      </figcaption>
                    </figure>}

                  {isOpen && !url && <div className="mt-6 max-w-2xl" data-id="element-3529" data-mp="39t3pnmzdw3kq">
                      <RegistrationForm webinar={webinar} mode="replay" onReplayReady={signedUrl => {
                setUrls(current => ({
                  ...current,
                  [webinar.slug]: signedUrl
                }));
                setOpen(null);
              }} data-id="element-3530" data-mp="3er53nthydcpc" />
                    </div>}
                </article>;
        })}
          </div>}
      </Section>
    </>;
}